// src/pages/InstructorDashboard.js
//
// REESCRITO — ya no usa gradingStore/chatStore/forumStore(mock) ni
// progressStore.getStudentsInCourse (que además era llamado de forma
// síncrona pese a ser async en la versión real).
//
// HUECOS REALES DEL BACKEND encontrados al migrar (no simplificaciones
// arbitrarias -- el dato simplemente no existe todavía en un endpoint):
//   - GET /entregas/pendientes-instructor NO devuelve courseId ni
//     isLate por entrega -- por eso el listado de "Entregas Pendientes"
//     ya no agrupa/filtra por curso ni marca tardanza.
//   - No existe un endpoint de "estadísticas de calificación por curso"
//     (graduadas/promedio) -- se aproxima el promedio con el promedio
//     de finalGrade de aprendizajeService.getEstudiantesCurso (mismo
//     dato real que usa StudentsPage), y se omite "calificadas" porque
//     no hay forma de contarlas sin ese endpoint.
//   - foroService.getTemas() está paginado (limit) -- las estadísticas
//     de foro por curso sólo cuentan los temas de la primera página
//     (hasta 20). Aproximación razonable para un resumen de dashboard,
//     no exacta para cursos con muchísimos temas.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Card, CardContent, Button, Avatar, Chip,
  List, ListItem, ListItemAvatar, ListItemText, ListItemSecondaryAction, IconButton,
  Divider, Paper, Badge, Tooltip, Alert, CircularProgress,
} from '@mui/material';
import {
  School as SchoolIcon, People as PeopleIcon, Assignment as AssignmentIcon,
  Forum as ForumIcon, Chat as ChatIcon, TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon, ArrowForward as ArrowForwardIcon, Grade as GradeIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AppContext';
import { cursoService, entregaService, chatService, foroService, meService, aprendizajeService } from '../services/api';

function InstructorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [courseStats, setCourseStats] = useState([]);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const cargarTodo = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const misInstituciones = await meService.getMisInstituciones();
      const institutionId = misInstituciones?.[0]?.id;

      const [{ data: cursosInst }, pendientes, convs] = await Promise.all([
        cursoService.getAll(institutionId ? { institutionId, limit: 50 } : { limit: 50 }),
        entregaService.getPendientesInstructor(),
        chatService.getConversaciones(),
      ]);

      const misCursos = cursosInst.filter((c) => c.instructorId === user?.id);

      const stats = await Promise.all(misCursos.map(async (curso) => {
        let avgScore = null;
        try {
          const { data: estudiantes } = await aprendizajeService.getEstudiantesCurso(curso.id, { limit: 200 });
          const conNota = estudiantes.filter((e) => e.finalGrade != null);
          avgScore = conNota.length > 0
            ? Math.round(conNota.reduce((sum, e) => sum + e.finalGrade, 0) / conNota.length)
            : null;
        } catch { /* deja avgScore en null si falla */ }

        let forumTopics = 0, forumPosts = 0;
        try {
          const { data: temas, total } = await foroService.getTemas(curso.id, 1, 20);
          forumTopics = total;
          forumPosts = temas.reduce((sum, t) => sum + (t.totalReplies || 0), 0);
        } catch { /* deja en 0 si falla */ }

        return {
          courseId: curso.id, courseName: curso.title,
          studentsCount: curso.totalEnrollments || 0,
          avgScore, forumTopics, forumPosts,
        };
      }));

      setCourseStats(stats);
      setPendingSubmissions(pendientes);
      setConversations(convs);
      setUnreadMessages(convs.reduce((sum, c) => sum + (c.unread || 0), 0));
    } catch (e) {
      setError(e?.message || 'No se pudo cargar el dashboard.');
    } finally {
      setCargando(false);
    }
  }, [user?.id]);

  useEffect(() => { cargarTodo(); }, [cargarTodo]);

  const totalStudents = courseStats.reduce((sum, c) => sum + c.studentsCount, 0);
  const totalPending = pendingSubmissions.length;

  const statsCards = [
    { title: 'Mis Cursos', value: courseStats.length, icon: <SchoolIcon />, color: '#3b82f6', action: () => navigate('/instructor/courses') },
    { title: 'Estudiantes', value: totalStudents, icon: <PeopleIcon />, color: '#10b981', action: () => navigate('/instructor/students') },
    { title: 'Por Calificar', value: totalPending, icon: <AssignmentIcon />, color: totalPending > 0 ? '#f59e0b' : '#6b7280', badge: totalPending > 0, action: () => navigate('/instructor/grading') },
    { title: 'Mensajes', value: unreadMessages, icon: <ChatIcon />, color: unreadMessages > 0 ? '#ef4444' : '#6b7280', badge: unreadMessages > 0, action: () => navigate('/instructor/chat') },
  ];

  if (cargando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
            <Avatar src={user?.avatar} sx={{ width: 80, height: 80, border: '3px solid', borderColor: 'primary.main' }}>
              {user?.name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={700}>¡Hola, {user?.name?.split(' ')[0]}! 👋</Typography>
              <Typography variant="body1" color="text.secondary">Instructor</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Chip icon={<SchoolIcon />} label={`${courseStats.length} curso${courseStats.length !== 1 ? 's' : ''}`} size="small" color="primary" variant="outlined" />
                <Chip icon={<PeopleIcon />} label={`${totalStudents} estudiantes`} size="small" variant="outlined" />
              </Box>
            </Box>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

        {totalPending > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}
            action={<Button color="inherit" size="small" onClick={() => navigate('/instructor/grading')}>Calificar ahora</Button>}>
            Tienes <strong>{totalPending} entrega{totalPending !== 1 ? 's' : ''}</strong> pendiente{totalPending !== 1 ? 's' : ''} de calificación.
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {statsCards.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card sx={{ cursor: 'pointer', transition: 'all 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }} onClick={stat.action}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>{stat.title}</Typography>
                      <Typography variant="h3" fontWeight={700}>
                        {stat.badge ? (
                          <Badge badgeContent={stat.value} color="error" max={99}><span style={{ paddingRight: 8 }}>{stat.value}</span></Badge>
                        ) : stat.value}
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: stat.color, width: 56, height: 56 }}>{stat.icon}</Avatar>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>📝 Entregas Pendientes</Typography>
                  <Button endIcon={<ArrowForwardIcon />} onClick={() => navigate('/instructor/grading')}>Ver todas</Button>
                </Box>

                {pendingSubmissions.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                    <Typography color="text.secondary">¡No hay entregas pendientes! 🎉</Typography>
                  </Box>
                ) : (
                  <List>
                    {pendingSubmissions.slice(0, 5).map((submission, index) => (
                      <React.Fragment key={submission.id}>
                        {index > 0 && <Divider />}
                        <ListItem sx={{ py: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => navigate(`/instructor/grading/${submission.id}`)}>
                          <ListItemAvatar><Avatar sx={{ bgcolor: 'primary.main' }}>{submission.studentName?.[0]}</Avatar></ListItemAvatar>
                          <ListItemText
                            primary={<Typography fontWeight={500}>{submission.studentName}</Typography>}
                            secondary={
                              <>
                                <Typography variant="body2" color="text.secondary">{submission.contentTitle}</Typography>
                                <Typography variant="caption" color="text.disabled">{new Date(submission.submittedAt).toLocaleDateString()}</Typography>
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Tooltip title="Calificar">
                              <IconButton edge="end" color="primary" onClick={(e) => { e.stopPropagation(); navigate(`/instructor/grading/${submission.id}`); }}>
                                <GradeIcon />
                              </IconButton>
                            </Tooltip>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>

            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>📚 Mis Cursos</Typography>
                {courseStats.length === 0 ? (
                  <Alert severity="info">No tienes cursos asignados todavía.</Alert>
                ) : (
                  <Grid container spacing={2}>
                    {courseStats.map((course) => (
                      <Grid item xs={12} md={6} key={course.courseId}>
                        <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer', transition: 'all 0.2s', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
                          onClick={() => navigate(`/instructor/course/${course.courseId}`)}>
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>{course.courseName}</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PeopleIcon fontSize="small" color="action" />
                                <Typography variant="body2">{course.studentsCount} estudiantes</Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrendingUpIcon fontSize="small" color="info" />
                                <Typography variant="body2">{course.avgScore != null ? `${course.avgScore}% promedio` : 'Sin datos'}</Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>💬 Mensajes Recientes</Typography>
                  <Badge badgeContent={unreadMessages} color="error">
                    <IconButton onClick={() => navigate('/instructor/chat')}><ChatIcon /></IconButton>
                  </Badge>
                </Box>

                {conversations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>No hay conversaciones</Typography>
                ) : (
                  <List dense>
                    {conversations.slice(0, 4).map((conv) => (
                      <ListItem key={conv.id} sx={{ borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate('/instructor/chat')}>
                        <ListItemAvatar>
                          <Badge badgeContent={conv.unread || 0} color="error" overlap="circular">
                            <Avatar src={conv.otherUserAvatar} sx={{ width: 36, height: 36 }}>{conv.otherUserName?.[0]}</Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={<Typography variant="body2" fontWeight={500} noWrap>{conv.otherUserName}</Typography>}
                          secondary={<Typography variant="caption" color="text.secondary" noWrap>{conv.lastMessage}</Typography>}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Button fullWidth variant="outlined" sx={{ mt: 1 }} onClick={() => navigate('/instructor/chat')}>Ver todos los mensajes</Button>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>🗣️ Foros</Typography>
                  <IconButton onClick={() => navigate('/instructor/forums')}><ForumIcon /></IconButton>
                </Box>

                {courseStats.map((course) => (
                  <Box key={course.courseId} sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={500}>{course.courseName}</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">📌 {course.forumTopics} temas</Typography>
                      <Typography variant="caption" color="text.secondary">💬 {course.forumPosts} respuestas</Typography>
                    </Box>
                  </Box>
                ))}

                <Button fullWidth variant="outlined" sx={{ mt: 1 }} onClick={() => navigate('/instructor/forums')}>Ir a los foros</Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>⚡ Acciones Rápidas</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button variant="contained" startIcon={<GradeIcon />} fullWidth onClick={() => navigate('/instructor/grading')}>Calificar entregas</Button>
                  <Button variant="outlined" startIcon={<PeopleIcon />} fullWidth onClick={() => navigate('/instructor/students')}>Ver estudiantes</Button>
                  <Button variant="outlined" startIcon={<ForumIcon />} fullWidth onClick={() => navigate('/instructor/forums')}>Moderar foros</Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default InstructorDashboard;
